import { useEffect, useState } from "react";
import {
  ShieldCheck, Sparkles, Loader2, Clock, CheckCircle2, X,
} from "lucide-react";
import { api } from "../../lib/api";
import BookExpertModal from "./BookExpertModal";

/** Verified Capital Readiness Score upsell.
 *
 *  States:
 *   - `none`     — show 50,000 KES CTA + "Why verified" benefits
 *   - `pending`  — show "Verified review in progress" status card
 *   - `verified` — show "Verified by Clinq" badge + verified score
 *
 *  Props:
 *   - verified: { status, verified_score, verified_at, paid_at } | null
 *   - indicativeScore: number (current AI score, shown as comparison)
 *   - compact: boolean — render a smaller variant for tight surfaces
 */
export default function VerifiedReadinessUpsell({ verified, indicativeScore, compact = false, project = false }) {
  const status = (verified && verified.status) || "none";
  const [open, setOpen] = useState(false);
  const [expertOpen, setExpertOpen] = useState(false);
  const [info, setInfo] = useState(null); // {price_kes, qualifies, min_score, revenue_stage}

  useEffect(() => {
    if (status !== "none") return;
    api.get("/founder/verified-readiness/status")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, [status]);

  // Auto-poll Paystack on return from checkout (works even if modal closed).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("verified_pending") !== "1") return undefined;
    const sid = params.get("reference") || params.get("trxref");
    if (!sid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/founder/verified-readiness/poll/${sid}`);
      } catch { /* ignore */ }
      if (!cancelled) {
        // Strip query string + force a reload so the upsell picks up the new status.
        window.location.replace(window.location.pathname);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === "verified") return <VerifiedBadgeCard verified={verified} project={project}/>;
  if (status === "pending") return <VerifiedPendingCard verified={verified} project={project}/>;

  // Below Promising (55+): invite to a free expert session instead of selling.
  if (info && !info.qualifies) {
    return (
      <>
        <NotYetQualifiedCard minScore={info.min_score} score={info.readiness_score}
                             onBook={() => setExpertOpen(true)}/>
        <BookExpertModal open={expertOpen} onClose={() => setExpertOpen(false)}/>
      </>
    );
  }

  return (
    <>
      <VerifiedCTA compact={compact} project={project} onClick={() => setOpen(true)}/>
      {open && (
        <VerifiedCheckoutModal
          indicativeScore={indicativeScore}
          project={project}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function NotYetQualifiedCard({ minScore, score, onBook }) {
  return (
    <div
      data-testid="verified-not-qualified-card"
      className="mt-6 rounded-2xl border border-[#0A8B85]/30 bg-[#E3F4EE] p-6 sm:p-7"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">
            <Sparkles className="h-3.5 w-3.5"/> Get funder-connection ready
          </p>
          <h3 className="mt-2 text-xl font-extrabold leading-tight text-neutral-900 sm:text-2xl">
            Not quite there yet — but you&apos;re on your way
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-neutral-700">
            Your score needs to reach the <b>Promising band ({minScore}+)</b> to qualify
            for verification and connection to funders — you&apos;re at <b>{score}</b>.
            Book a <b>free session with a Clinq expert</b> to get there faster.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            data-testid="verified-book-expert-btn"
            onClick={onBook}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667]"
          >
            <Sparkles className="h-4 w-4"/> Book a free expert session
          </button>
          <p className="text-[10px] text-neutral-500">First 30-minute session is free</p>
        </div>
      </div>
    </div>
  );
}

function VerifiedCTA({ compact, onClick, project = false }) {
  const noun = project ? "project" : "business";
  return (
    <div
      data-testid="verified-readiness-cta"
      className="mt-6 overflow-hidden rounded-2xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#1c1306] via-[#3a2710] to-[#0a0a0a] p-6 text-white sm:p-7"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F59E0B]">
            <ShieldCheck className="h-3.5 w-3.5"/> Verified &amp; funder-connected
          </p>
          <h3 className={`mt-2 ${compact ? "text-xl" : "text-2xl sm:text-3xl"} font-extrabold leading-tight`}>
            Get your {noun} verified and connected to funders
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-white/85">
            Your score qualifies for funder connection. A Clinq Expert hand-reviews your
            {" "}{noun} and issues a
            <b className="text-[#F59E0B]"> Verified {project ? "Project" : "Capital"} Readiness Score</b> — then we
            connect you to funders whose mandates fit your {noun}. Verification builds
            trust with institutional investors and materially improves your chances of
            securing capital.
          </p>

          {!compact && (
            <ul className="mt-4 grid grid-cols-1 gap-1.5 text-sm text-white/85 sm:grid-cols-2">
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#7BD3CE]"/> Hand-reviewed by a Clinq Expert</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#7BD3CE]"/> Connected to funders whose mandates fit</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#7BD3CE]"/> &ldquo;Verified by Clinq&rdquo; seal on your report + share link</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#7BD3CE]"/> Direct feedback on what funders will challenge</li>
            </ul>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Included</p>
            <p className="text-4xl font-extrabold text-[#F59E0B]" data-testid="verified-price">
              Free
            </p>
            <p className="text-[10px] text-white/50">
              Included with your {project ? "Project" : "Capital"} Readiness Report
            </p>
          </div>
          <button
            data-testid="verified-readiness-buy-btn"
            onClick={onClick}
            className="inline-flex items-center gap-2 rounded-lg bg-[#F59E0B] px-5 py-2.5 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]"
          >
            <ShieldCheck className="h-4 w-4"/>
            Get verified &amp; connected
          </button>
          <p className="text-[10px] text-white/40">No extra charge</p>
        </div>
      </div>
    </div>
  );
}

function VerifiedPendingCard({ verified, project = false }) {
  const paidAt = verified?.paid_at
    ? new Date(verified.paid_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : "—";
  return (
    <div data-testid="verified-pending-card"
         className="mt-6 rounded-2xl border border-[#F59E0B]/40 bg-amber-50 p-5">
      <div className="flex items-start gap-3">
        <Clock className="mt-0.5 h-6 w-6 flex-shrink-0 text-[#B45309]"/>
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-[#B45309]">
            Verified review in progress
          </p>
          <p className="mt-1 text-base font-bold text-neutral-900">
            A Clinq Expert is reviewing your {project ? "project" : "business"}.
          </p>
          <p className="mt-1 text-sm text-neutral-700">
            We&rsquo;ll email you when your <b>Verified {project ? "Project" : "Capital"} Readiness Score</b> is ready
            — typically within 5 business days. Requested on {paidAt}.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700">
            <Sparkles className="h-3.5 w-3.5 text-[#0A8B85]"/>
            Your Indicative score is locked while the review is in progress.
          </p>
        </div>
      </div>
    </div>
  );
}

function VerifiedBadgeCard({ verified, project = false }) {
  const verifiedAt = verified?.verified_at
    ? new Date(verified.verified_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : "—";
  return (
    <div data-testid="verified-badge-card"
         className="mt-6 overflow-hidden rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
            <ShieldCheck className="h-6 w-6"/>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
              Verified by Clinq
            </p>
            <p className="text-base font-bold text-neutral-900">
              Verified {project ? "Project" : "Capital"} Readiness Score
            </p>
            <p className="text-xs text-neutral-600">Verified on {verifiedAt}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Verified Score</p>
          <p className="text-5xl font-extrabold text-emerald-700"
             data-testid="verified-readiness-score">
            {verified?.verified_score ?? "—"}
            <span className="text-base text-neutral-500"> / 100</span>
          </p>
        </div>
      </div>
      {verified?.verified_notes && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-white p-4 text-sm text-neutral-700">
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Reviewer notes</p>
          <p className="mt-1 whitespace-pre-wrap">{verified.verified_notes}</p>
        </div>
      )}
    </div>
  );
}

function VerifiedCheckoutModal({ indicativeScore, onClose, project = false }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const goCheckout = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/founder/verified-readiness/checkout",
        { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Request failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="verified-checkout-modal"
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">
              <ShieldCheck className="h-3.5 w-3.5"/> Verified upgrade
            </p>
            <h3 className="mt-1 text-lg font-bold text-neutral-900">
              Verify your {project ? "Project" : "Capital"} Readiness Score
            </h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100">
            <X className="h-5 w-5"/>
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Indicative (now)</p>
            <p className="mt-1 text-3xl font-extrabold text-neutral-900">{indicativeScore || 0}<span className="text-sm text-neutral-400"> /100</span></p>
            <p className="mt-1 text-[11px] text-neutral-500">Generated from your documents</p>
          </div>
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Verified (after)</p>
            <p className="mt-1 text-3xl font-extrabold text-emerald-700">+ Verified seal</p>
            <p className="mt-1 text-[11px] text-emerald-700">Hand-reviewed by a Clinq Expert</p>
          </div>
        </div>

        <ul className="mt-4 space-y-2 text-sm text-neutral-700">
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600"/> Materially higher chance of funding from institutional capital providers</li>
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600"/> Significantly increases the credibility of your Capital Readiness Report</li>
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600"/> &ldquo;Verified by Clinq&rdquo; badge on your report + public share link</li>
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600"/> Direct expert feedback delivered within 5 business days</li>
        </ul>

        <div className="mt-5 flex items-end justify-between gap-3 rounded-xl border border-[#F59E0B]/30 bg-amber-50 p-4">          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">Included</p>
            <p className="text-3xl font-extrabold text-neutral-900" data-testid="verified-modal-price">
              Free
            </p>
          </div>
          <p className="max-w-[200px] text-right text-[11px] text-neutral-600">
            Included with your {project ? "Project" : "Capital"} Readiness Report — no extra charge.
            Includes the verified seal, expert reviewer notes + funder connection.
          </p>
        </div>

        {err && <p className="mt-3 text-sm text-red-600">{err}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-200 disabled:opacity-50">
            Maybe later
          </button>
          <button
            onClick={goCheckout}
            disabled={busy}
            data-testid="verified-readiness-checkout"
            className="inline-flex items-center gap-2 rounded-lg bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <ShieldCheck className="h-4 w-4"/>}
            {busy ? "Submitting…" : "Request expert review"}
          </button>
        </div>
        <p className="mt-2 text-center text-[11px] text-neutral-500">
          Your Indicative score is locked while the review is in progress.
        </p>
      </div>
    </div>
  );
}
